import { useState, useContext } from "react";
import { Mail, Lock, Eye, EyeOff, Zap, TrendingUp, Users, Shield } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { useAlert }    from "../../hooks/useAlert";
import authService     from "../../services/auth.service";

/* ── Neural-network nodes & edges ─────────────────────────── */
const NODES = [
  { id:  0, x:  58, y:  75 }, { id:  1, x: 160, y:  45 }, { id:  2, x: 265, y:  88 },
  { id:  3, x: 355, y:  55 }, { id:  4, x: 432, y: 108 }, { id:  5, x:  38, y: 178 },
  { id:  6, x: 132, y: 158 }, { id:  7, x: 225, y: 198 }, { id:  8, x: 318, y: 168 },
  { id:  9, x: 405, y: 198 }, { id: 10, x:  78, y: 278 }, { id: 11, x: 190, y: 298 },
  { id: 12, x: 292, y: 268 }, { id: 13, x: 392, y: 288 }, { id: 14, x: 128, y: 378 },
  { id: 15, x: 248, y: 368 }, { id: 16, x: 362, y: 398 }, { id: 17, x: 168, y: 465 },
  { id: 18, x: 298, y: 478 },
];

const EDGES = [
  [0,1],[1,2],[2,3],[3,4],
  [0,5],[0,6],[1,6],[1,7],[2,7],[2,8],[3,8],[3,9],[4,9],
  [5,10],[6,10],[6,11],[7,11],[7,12],[8,12],[8,13],[9,13],
  [10,14],[11,14],[11,15],[12,15],[12,16],[13,16],
  [14,17],[15,17],[15,18],[16,18],
];

const FLOW_EDGES = [1, 4, 8, 12, 16, 20, 24, 27];

function NetworkSVG() {
  return (
    <svg
      viewBox="0 0 480 520"
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.35 }}
      aria-hidden="true"
    >
      {/* Static edges */}
      {EDGES.map(([a, b], i) => {
        const na = NODES[a], nb = NODES[b];
        return (
          <line
            key={i}
            x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke="var(--color-hero-edge)"
            strokeWidth="0.9"
          />
        );
      })}

      {/* Animated flow packets */}
      {FLOW_EDGES.map((ei, fi) => {
        const edge = EDGES[ei];
        if (!edge) return null;
        const na = NODES[edge[0]], nb = NODES[edge[1]];
        return (
          <line
            key={`flow-${fi}`}
            x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke="var(--color-hero-flow)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeDasharray="8 500"
            style={{
              animation: `flowDash ${2.2 + fi * 0.45}s linear infinite`,
              animationDelay: `${fi * 0.55}s`,
            }}
          />
        );
      })}

      {/* Node outer rings */}
      {NODES.map((n, i) => (
        <circle
          key={`ring-${n.id}`}
          cx={n.x} cy={n.y}
          r={i % 5 === 0 ? 11 : 7}
          fill="none"
          stroke="var(--color-hero-edge)"
          strokeWidth="1"
          style={{
            animation: `pulseNode ${2.8 + (i % 3) * 0.6}s ease-in-out infinite`,
            animationDelay: `${i * 0.18}s`,
            transformOrigin: `${n.x}px ${n.y}px`,
          }}
        />
      ))}

      {/* Node cores */}
      {NODES.map((n, i) => (
        <circle
          key={`core-${n.id}`}
          cx={n.x} cy={n.y}
          r={i % 5 === 0 ? 3.5 : 2.2}
          fill="var(--color-hero-node)"
        />
      ))}
    </svg>
  );
}

/* ── Feature cards ────────────────────────────────────────── */
const FEATURES = [
  { Icon: Zap,        label: "AI-Powered Sends",      desc: "Smart send-time optimization"  },
  { Icon: TrendingUp, label: "Live Analytics",         desc: "Real-time campaign dashboards" },
  { Icon: Users,      label: "Audience Segments",      desc: "Precision targeting at scale"  },
  { Icon: Shield,     label: "99.9% Deliverability",   desc: "Authenticated, trusted sends"  },
];

const STATS = [
  { value: "2.4M",  label: "Emails sent" },
  { value: "98.7%", label: "Delivery rate" },
  { value: "34K",   label: "Active contacts" },
];

/* ── Component ────────────────────────────────────────────── */
export default function Login() {
  const { login }     = useContext(AuthContext);
  const { showAlert } = useAlert();

  const [form, setForm]         = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      showAlert({ type: "error", message: "Please fill in all fields." });
      return;
    }

    if (form.email === "admin123" && form.password === "admin123") {
      const mockUser = { id: 1, name: "Admin", email: "admin@suryagroup.us", role: "admin" };
      localStorage.setItem("mock_user", JSON.stringify(mockUser));
      login("mock_admin_session", mockUser);
      return;
    }

    setLoading(true);
    try {
      const res = await authService.login(form);
      const { token, user } = res.data?.data ?? res.data;
      login(token, user);
    } catch (err) {
      showAlert({
        type: "error",
        message: err.response?.data?.message ?? "Invalid credentials. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">

      {/* ════════════════════════════════════════
          LEFT — Hero / AI visualization panel
          ════════════════════════════════════════ */}
      <div
        className="hidden lg:flex lg:w-[58%] xl:w-3/5 relative overflow-hidden flex-col justify-between p-12"
        style={{
          background: "linear-gradient(140deg, var(--color-hero) 0%, var(--color-hero-end) 100%)",
        }}
      >
        {/* Floating glow orbs */}
        <div
          className="absolute w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{
            background: "var(--color-hero-orb1)",
            filter: "blur(90px)",
            top: "8%", left: "18%",
            animation: "floatOrb1 14s ease-in-out infinite",
          }}
        />
        <div
          className="absolute w-[340px] h-[340px] rounded-full pointer-events-none"
          style={{
            background: "var(--color-hero-orb2)",
            filter: "blur(110px)",
            top: "48%", right: "8%",
            animation: "floatOrb2 17s ease-in-out infinite",
          }}
        />
        <div
          className="absolute w-[260px] h-[260px] rounded-full pointer-events-none"
          style={{
            background: "var(--color-hero-orb1)",
            filter: "blur(70px)",
            bottom: "12%", left: "4%",
            animation: "floatOrb3 11s ease-in-out infinite",
          }}
        />

        {/* Scanning line */}
        <div
          className="absolute left-0 right-0 h-px pointer-events-none z-0"
          style={{
            background: "linear-gradient(90deg, transparent 0%, rgba(180,180,255,0.45) 50%, transparent 100%)",
            animation: "heroScanLine 9s linear infinite",
            top: 0,
          }}
        />

        {/* Network SVG (z-0) */}
        <div className="absolute inset-0 z-0">
          <NetworkSVG />
        </div>

        {/* ── Content z-10 ── */}
        <div className="relative z-10">
          {/* Logo mark */}
          <div className="flex items-center gap-3 mb-14">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: "rgba(124,124,191,0.22)",
                border: "1px solid rgba(124,124,191,0.38)",
              }}
            >
              <Mail className="w-5 h-5" style={{ color: "rgba(195,195,255,1)" }} />
            </div>
            <div>
              <div className="text-sm font-semibold" style={{ color: "rgba(225,225,255,0.95)" }}>
                SwiftReach
              </div>
              <div className="text-xs" style={{ color: "rgba(140,140,195,0.72)" }}>
                by Surya Group
              </div>
            </div>
          </div>

          {/* Headline block */}
          <div style={{ animation: "fadeSlideUp 0.8s ease-out both" }}>
            {/* Live badge */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-5"
              style={{
                background: "rgba(124,124,191,0.14)",
                border: "1px solid rgba(124,124,191,0.32)",
                color: "rgba(190,190,255,0.92)",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full inline-block"
                style={{
                  background: "rgba(74,222,128,1)",
                  boxShadow: "0 0 6px rgba(74,222,128,0.7)",
                  animation: "blinkDot 2.2s ease-in-out infinite",
                }}
              />
              Agentic AI Engine · Live
            </div>

            <h1
              className="text-[2.6rem] font-bold leading-[1.15] tracking-tight mb-4"
              style={{ color: "rgba(235,235,255,0.97)" }}
            >
              Intelligent<br />
              Mail Marketing<br />
              <span style={{ color: "rgba(165,165,230,0.82)" }}>at Scale</span>
            </h1>

            <p
              className="text-sm leading-relaxed max-w-[340px]"
              style={{ color: "rgba(155,155,200,0.82)" }}
            >
              Surya Group's AI-powered platform autonomously segments, personalizes,
              and delivers campaigns with precision and speed.
            </p>
          </div>
        </div>

        {/* ── Stats + Feature grid ── */}
        <div className="relative z-10">
          {/* Stat row */}
          <div
            className="flex gap-8 mb-8"
            style={{ animation: "fadeSlideUp 0.8s ease-out 0.18s both" }}
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <div
                  className="text-2xl font-bold tracking-tight"
                  style={{ color: "rgba(220,220,255,0.96)" }}
                >
                  {s.value}
                </div>
                <div className="text-xs mt-0.5" style={{ color: "rgba(130,130,185,0.75)" }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Feature cards 2×2 */}
          <div
            className="grid grid-cols-2 gap-3"
            style={{ animation: "fadeSlideUp 0.8s ease-out 0.36s both" }}
          >
            {FEATURES.map(({ Icon, label, desc }) => (
              <div
                key={label}
                className="flex items-start gap-3 p-3.5 rounded-2xl"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(124,124,191,0.18)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5"
                  style={{ background: "rgba(124,124,191,0.22)" }}
                >
                  <Icon className="w-4 h-4" style={{ color: "rgba(185,185,255,0.9)" }} />
                </div>
                <div>
                  <div
                    className="text-xs font-semibold leading-tight"
                    style={{ color: "rgba(210,210,255,0.92)" }}
                  >
                    {label}
                  </div>
                  <div
                    className="text-xs mt-0.5 leading-tight"
                    style={{ color: "rgba(120,120,175,0.78)" }}
                  >
                    {desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          RIGHT — Login form panel
          ════════════════════════════════════════ */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-[390px]">

          {/* Mobile-only brand mark */}
          <div className="lg:hidden text-center mb-8">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
              style={{ background: "var(--color-primary)" }}
            >
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-lg font-semibold text-text_primary">SwiftReach</h1>
            <p className="text-xs text-text_muted mt-0.5">Mail Marketing Suite · Surya Group</p>
          </div>

          {/* Card */}
          <div
            className="bg-surface border border-border rounded-2xl p-8"
            style={{ boxShadow: "0 8px 48px rgba(0,0,0,0.09)" }}
          >
            <div className="mb-7">
              <h2 className="text-2xl font-bold text-text_primary tracking-tight">
                Welcome back
              </h2>
              <p className="text-sm text-text_muted mt-1.5">
                Sign in to your SwiftReach account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-text_secondary mb-1.5 uppercase tracking-widest">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text_muted pointer-events-none" />
                  <input
                    type="text"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@suryagroup.us"
                    autoComplete="email"
                    className="w-full pl-10 pr-4 py-3 text-sm rounded-xl bg-background border border-border text-text_primary placeholder:text-text_muted focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-text_secondary uppercase tracking-widest">
                    Password
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-xs text-primary hover:text-primary_hover transition-colors"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text_muted pointer-events-none" />
                  <input
                    type={showPass ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="w-full pl-10 pr-11 py-3 text-sm rounded-xl bg-background border border-border text-text_primary placeholder:text-text_muted focus:outline-none focus:border-primary transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((s) => !s)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text_muted hover:text-text_primary transition-colors"
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed transition-all mt-2"
                style={{
                  background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)",
                  boxShadow: loading ? "none" : "0 4px 20px rgba(53,53,93,0.28)",
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in…
                  </span>
                ) : (
                  "Sign in →"
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-text_muted mt-5">
            Restricted access · Authorized personnel only
          </p>
        </div>
      </div>
    </div>
  );
}
